import { useSyncExternalStore } from 'react';
import { ACTIVE_GENERATION_STORAGE_KEY } from './reputation-defaults.constants';

export interface ActiveGeneration {
  jobId: string;
  /** Epoch millis. Lets the banner stop waiting on a run that never reports back. */
  startedAt: number;
}

const listeners = new Set<() => void>();

const notify = () => listeners.forEach(listener => listener());

/**
 * The run outlives the dialog, so its id is kept in storage — a partner who closes the
 * dialog or reloads still finds out how a one-shot generation ended. Writes notify
 * subscribers so a banner already on screen picks the run up without a reload.
 */
export function rememberActiveGeneration(jobId: string): void {
  const active: ActiveGeneration = { jobId, startedAt: Date.now() };
  localStorage.setItem(ACTIVE_GENERATION_STORAGE_KEY, JSON.stringify(active));
  notify();
}

export function forgetActiveGeneration(): void {
  localStorage.removeItem(ACTIVE_GENERATION_STORAGE_KEY);
  notify();
}

export function readActiveGeneration(): ActiveGeneration | undefined {
  const raw = localStorage.getItem(ACTIVE_GENERATION_STORAGE_KEY);
  if (!raw) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as ActiveGeneration).jobId === 'string' &&
      typeof (parsed as ActiveGeneration).startedAt === 'number'
    ) {
      return parsed as ActiveGeneration;
    }
  } catch {
    // Not JSON: fall through and drop it.
  }
  localStorage.removeItem(ACTIVE_GENERATION_STORAGE_KEY);
  return undefined;
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === ACTIVE_GENERATION_STORAGE_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
};

/** Snapshots must be referentially stable between writes or useSyncExternalStore loops. */
const getSnapshot = () => localStorage.getItem(ACTIVE_GENERATION_STORAGE_KEY);

export function useActiveGeneration(): ActiveGeneration | undefined {
  const raw = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return raw === null ? undefined : readActiveGeneration();
}
